import { useState, type ReactNode } from 'react';
import { useFeature, useSession } from '../Session.jsx';
import { Button, Empty, Link } from '../components/primitives.jsx';
import { Shell } from '../components/Shell.jsx';
import { hrefFor } from '../lib/router.js';
import { twitchConnection } from '../lib/twitchConnection.js';

/**
 * Connect the Twitch channel you broadcast.
 *
 * Self-serve: anybody signed in may connect their OWN channel, and Twitch's
 * consent screen is the proof that it is theirs. The server brings them back
 * to the new channel as its owner.
 */
function ConnectChannel({ lead }: { lead: string }): ReactNode {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      window.location.assign(await twitchConnection.start());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the Twitch connection.');
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 rounded-lg border border-ink-800 p-5">
      <p className="text-sm text-ink-200">{lead}</p>
      <p className="mt-1 text-xs text-ink-400">
        You will approve tomo on Twitch, signed in as the channel you want to connect.
      </p>
      <div className="mt-4">
        <Button onClick={() => { void connect(); }} disabled={busy}>
          Connect your Twitch channel
        </Button>
      </div>
      {error ? <p className="mt-3 text-sm text-bad-400">{error}</p> : null}
    </div>
  );
}

export function ChannelPicker(): ReactNode {
  const session = useSession();
  const collectibles = useFeature('collectibles');

  /**
   * Signed in and belonging to nothing is an ordinary state — it is every
   * user's first moment, and on a fresh deployment it is the only state that
   * exists until somebody connects a channel.
   *
   * Without this the page rendered "Which channel?" above an empty list,
   * which reads as a broken page rather than an answer. It is the visible
   * half of a bootstrap deadlock whose other half was the API refusing the
   * session outright.
   */
  if (session.channels.length === 0) {
    return (
      <Shell>
      <div className="max-w-2xl">
        <Empty
          title={`Signed in as ${session.user.display_name}.`}
          hint="You do not administer any channel yet."
        />
        <ConnectChannel lead="Stream on Twitch? Connect your channel and you are its owner." />
        {collectibles ? (
          <p className="mt-6 text-center text-xs text-ink-400">
            Your collection and trades work already, across every channel you join.{' '}
            <Link href={hrefFor(['collection'])}>Open your collection</Link>
          </p>
        ) : null}
      </div>
      </Shell>
    );
  }

  return (
    <Shell>
    <div className="max-w-2xl">
      <h1 className="text-lg font-semibold">Which channel?</h1>
      <ul className="mt-6 space-y-2">
        {session.channels.map((channel) => (
          <li key={channel.id}>
            <Link
              href={hrefFor([channel.id])}
              className="flex items-center justify-between rounded-lg border border-ink-800 px-4 py-3 hover:border-ink-700"
            >
              <span>
                <span className="block text-ink-50">{channel.display_name}</span>
                <span className="block text-xs text-ink-400">{channel.slug}</span>
              </span>
              <span className="text-xs text-ink-400">{channel.role}</span>
            </Link>
          </li>
        ))}
      </ul>
      {/* Somebody who runs other people's channels may still stream themselves. */}
      {session.channels.some((c) => c.role === 'owner')
        ? null
        : <ConnectChannel lead="Stream on Twitch yourself? Connect your own channel." />}
    </div>
    </Shell>
  );
}
